import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { AttendanceScreen } from '@/features/hr-attendance/attendance-screen'
import {
  type AttendanceSearch,
  attendanceSearchSchema,
  movedRequestsTab,
} from '@/features/hr-attendance/search'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for attendance, following `routes/_authenticated/users.tsx`.
 *
 * The route sits under `_authenticated` so the layout's `beforeLoad` guard has
 * resolved the session before the screen mounts.
 *
 * `validateSearch` is what puts the filters in the URL: a filtered view is
 * linkable and survives a reload, and the back button steps through filter
 * states (`apps/company/AGENTS.md`). The schema fills every default, so
 * `/hr/attendance` with no query string is already a useful screen.
 *
 * The grant guard is presentation only. The API enforces `attendance.view` on
 * every read, `attendance.create` on the punches and `attendance.export` on the
 * downloads, and the module gate refuses the whole `/api/hr` prefix for a group
 * whose plan does not include HR — a stale grant still 403s server-side. This
 * only keeps a user without the grant out of a screen they cannot use.
 *
 * Per the route-shell rule, this file is a route definition, a guard and a
 * render. The screen lives in `src/features/hr-attendance/`.
 */
export const Route = createFileRoute('/_authenticated/hr/attendance')({
  validateSearch: attendanceSearchSchema,
  beforeLoad: ({ context, location }) => {
    if (!hasGrant(context.queryClient, 'attendance.view')) {
      throw redirect({ to: '/' })
    }
    // The request queues moved to attendance requests; an old link follows them.
    const raw = location?.search as Record<string, unknown> | undefined
    const moved = movedRequestsTab(raw)
    if (moved) {
      throw redirect({
        to: '/hr/attendance-corrections',
        search: {
          tab: moved,
          entry_id: typeof raw?.entry_id === 'string' ? raw.entry_id : undefined,
        },
      })
    }
  },
  component: HrAttendanceRoute,
})

function HrAttendanceRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch()

  return (
    <AttendanceScreen
      search={search}
      onSearchChange={(patch: Partial<AttendanceSearch>) => {
        // The merged object rather than a reducer: the reducer's parameter is
        // typed as the union of every route's search params, which is not this
        // route's shape. `search` is already this route's, validated.
        void navigate({
          to: '/hr/attendance',
          search: { ...search, ...patch },
          replace: true,
        })
      }}
    />
  )
}
